// vg-binding: CONTEXT:decisions (P999.11.D-09/D-14/D-15/D-16/D-04)
// vg-binding: INTERFACE-STANDARDS:error-shape
// apps/admin/src/pages/products/CategoriesPage.tsx
//
// Category & Collection — route /products/categories.
//
// vg-binding: CONTEXT:decisions=P8.D-90,P8.D-294,P8.D-295,P8.D-296,P8.D-297,P8.D-298,P8.D-321,P8.D-322
//
// dbg-20260803-025413 — built against the Figma "Fulfillment V3" frames
// `category-management` and `collection-management`. Those are ONE page with
// two tabs, not two routes: same header, same toolbar, same table, same
// footer. Collection differs only by an extra LABEL column and the create
// button's label, so the tab switch swaps the data source and passes
// `showLabel` — it does not navigate away.
//
// Layout numbers come from the Figma node tree (11:4):
//   content padding 24, section gap 24
//   tab bar        h=47, 1px bottom rule, active tab = 2px brand underline
//   toolbar        search 260x32, filters 104/110x32, all r=6
//   footer         3 zones — rows-per-page left, "Showing x–y of N" CENTRE,
//                  numbered pages right
//
// The footer is hand-rolled rather than using the shared <Pagination>: that
// component packs rows-per-page next to the page buttons on one side, which
// is a different layout from the design's three zones.
//
// URL state: tab / search / status / display / page / pageSize all live in the
// query string so a filtered view is linkable and survives a refresh.
//
// Row order and pagination belong to the server (Category.md BR-2 newest
// first, BR-3 children under their parent); this page never re-sorts.
//
// Dragging a row changes its POSITION among its own siblings, never its
// parent (dbg-20260811-022808, reversing dbg-20260808-031625). Re-parenting
// has one surface: Edit → Parent Category. TaxonomyTable owns the same-level
// rule and hands back the level in its new order; this page only turns that
// into sortOrder values, issues the write, and reports it.

import { useCallback, useMemo, useState, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button, NativeSelect, SearchInput, Spinner, cn, useToast, PageHeader } from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  CategoryApiError,
  categoryQueryKeys,
  listAdminCategoriesPaged,
  reorderAdminCategories,
  updateAdminCategory,
  type CategoryRow,
  type CategoryStatus,
  type ListCategoriesPagedFilters,
} from '../../features/products/category.api.js';
import {
  CollectionApiError,
  collectionQueryKeys,
  listAdminCollectionsPaged,
  reorderAdminCollections,
  updateAdminCollection,
  type CollectionRow,
  type ListCollectionsPagedFilters,
} from '../../features/products/collection.api.js';
import { SelectProductsDialog } from '../../features/products/SelectProductsDialog.js';
import { TaxonomyTable } from '../../features/products/TaxonomyTable.js';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

type Tab = 'category' | 'collection';
type StatusFilter = '' | CategoryStatus;
type DisplayFilter = '' | 'on' | 'off';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CategoriesPage(): JSX.Element {
  const { t } = useTranslation('catalog');
  const { toast } = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: Tab = searchParams.get('tab') === 'collection' ? 'collection' : 'category';
  const isCollection = tab === 'collection';

  const search = searchParams.get('search') ?? '';
  const statusFilter = (searchParams.get('status') ?? '') as StatusFilter;
  const displayFilter = (searchParams.get('display') ?? '') as DisplayFilter;
  const page = Number.parseInt(searchParams.get('page') ?? '1', 10) || 1;
  const pageSize =
    Number.parseInt(searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE), 10) ||
    DEFAULT_PAGE_SIZE;

  const patchParams = useCallback(
    (patch: Record<string, string | null>, resetPage = true): void => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      // Any filter change invalidates the page index — page 3 of the
      // unfiltered list is meaningless once the result set shrinks.
      if (resetPage) next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  function selectTab(next: Tab): void {
    // Switching tabs clears the filters: Status/Display/search apply to a
    // different result set, and carrying them over silently hides rows.
    const params = new URLSearchParams();
    if (next === 'collection') params.set('tab', 'collection');
    setSearchParams(params, { replace: true });
  }

  const sharedFilters = useMemo(
    () => ({
      page,
      pageSize,
      ...(search !== '' ? { search } : {}),
      ...(statusFilter !== '' ? { status: statusFilter } : {}),
      ...(displayFilter !== '' ? { display: displayFilter === 'on' } : {}),
    }),
    [page, pageSize, search, statusFilter, displayFilter],
  );

  // --- Data ----------------------------------------------------------------

  const categoryQuery = useQuery({
    queryKey: categoryQueryKeys.paged(sharedFilters as ListCategoriesPagedFilters),
    queryFn: () => listAdminCategoriesPaged(sharedFilters as ListCategoriesPagedFilters),
    enabled: !isCollection,
    staleTime: 30_000,
  });

  const collectionQuery = useQuery({
    queryKey: collectionQueryKeys.paged(sharedFilters as ListCollectionsPagedFilters),
    queryFn: () => listAdminCollectionsPaged(sharedFilters as ListCollectionsPagedFilters),
    enabled: isCollection,
    staleTime: 30_000,
  });

  const activeQuery = isCollection ? collectionQuery : categoryQuery;
  const total = activeQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // --- Mutations -----------------------------------------------------------

  function reportError(err: unknown, fallbackKey: string): void {
    const msg =
      err instanceof CategoryApiError || err instanceof CollectionApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : t(fallbackKey);
    toast({ variant: 'error', title: msg });
  }

  function invalidateActive(): Promise<unknown> {
    return qc.invalidateQueries({
      queryKey: isCollection ? collectionQueryKeys.all : categoryQueryKeys.all,
    });
  }

  const toggleMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Record<string, unknown> }) =>
      isCollection ? updateAdminCollection(id, dto) : updateAdminCategory(id, dto),
    onSuccess: async () => {
      await invalidateActive();
      // A toggle used to succeed silently: the switch moved and nothing
      // confirmed the server had accepted it, which is indistinguishable from
      // optimistic-only UI.
      toast({ variant: 'success', title: t('category.list.toggleSaved') });
    },
    onError: (err) => {
      reportError(err, 'category.list.toggleFailed');
      void invalidateActive();
    },
  });

  /**
   * Drag-drop moves a row among its OWN siblings — position only, never
   * parentage (dbg-20260811-022808). The payload is a relative order and the
   * server renumbers the whole level from it, so a drag on page 2 does not
   * collide with page 1. Cross-level payloads are refused there too
   * (409 CATEGORY_REORDER_CROSS_LEVEL), which is the backstop for the
   * same-level check the table already makes.
   */
  const reorderMutation = useMutation({
    mutationFn: (items: Array<{ id: string; sortOrder: number }>) =>
      isCollection
        ? reorderAdminCollections({ items })
        : reorderAdminCategories({ items }),
    onSuccess: async () => {
      await invalidateActive();
      toast({ variant: 'success', title: t('category.list.reorderSaved') });
    },
    onError: (err) => {
      // A rejected move must not leave the row rendered in its new slot — the
      // drag already moved it optimistically in the user's head.
      reportError(err, 'category.page.reorderFailed');
      void invalidateActive();
    },
  });

  /** Row whose products picker is open, plus its current links. */
  const [productsFor, setProductsFor] = useState<{ id: string; ids: string[] } | null>(
    null,
  );

  const busy = toggleMutation.isPending || reorderMutation.isPending;
  // `isFetching` covers the background refetch after an invalidate, which
  // `isLoading` does not — without it the table shows stale rows with no hint
  // that fresher ones are on the way.
  const loading = busy || activeQuery.isFetching;

  // --- Handlers ------------------------------------------------------------

  const basePath = isCollection ? '/products/collections' : '/products/categories';

  function handleToggleStatus(row: { id: string }, next: boolean): void {
    toggleMutation.mutate({ id: row.id, dto: { status: next ? 'Active' : 'Inactive' } });
  }
  function handleToggleDisplay(row: { id: string }, next: boolean): void {
    toggleMutation.mutate({ id: row.id, dto: { display: next } });
  }
  function handleReorder(siblings: Array<{ id: string }>): void {
    // sortOrder = index * 10 leaves gaps for future single-row inserts.
    reorderMutation.mutate(siblings.map((c, idx) => ({ id: c.id, sortOrder: idx * 10 })));
  }
  function handleEdit(row: { id: string }): void {
    navigate(`${basePath}/${row.id}/edit`);
  }

  /**
   * The per-row products action opens the picker in place rather than routing
   * to the edit form (dbg-20260803-100937). The admin is asking "what is in
   * this row?", and sending them to a form they then have to scroll and read
   * answers a different question. It opens with "Show selected only" on, so
   * the answer is the first thing on screen.
   */
  function handleViewProducts(row: { id: string; blueprintIds: string[] }): void {
    setProductsFor({ id: row.id, ids: row.blueprintIds });
  }

  // --- Render --------------------------------------------------------------

  const tabBtn = (value: Tab, label: string): JSX.Element => {
    const active = tab === value;
    return (
      <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => selectTab(value)}
        data-testid={`categories-tab-${value}`}
        className={cn(
          '-mb-px border-b-2 px-4 pb-3 pt-2 text-sm transition-colors',
          active
            ? 'border-pw-primary font-semibold text-pw-primary'
            : 'border-transparent font-medium text-pw-text-muted hover:text-pw-text',
        )}
      >
        {label}
      </button>
    );
  };

  return (
    <main className="flex flex-col gap-6 p-6" data-testid="admin-categories-page">
      <PageHeader title={t('category.list.pageTitle')} subtitle={t('category.list.pageSubtitle')} />

      <div
        role="tablist"
        aria-label={t('category.list.pageTitle')}
        className="flex items-end border-b border-pw-border"
      >
        {tabBtn('category', t('category.list.tabCategory'))}
        {tabBtn('collection', t('category.list.tabCollection'))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* SearchInput, not a plain Input: it debounces 350ms internally.
            A raw Input wired straight to patchParams fired one request per
            keystroke — typing "apparel" was 7 list queries and 7 history
            entries. */}
        <SearchInput
          value={search}
          onValueChange={(next) => patchParams({ search: next })}
          placeholder={t(
            isCollection
              ? 'collection.list.searchPlaceholder'
              : 'category.list.searchPlaceholder',
          )}
          aria-label={t(
            isCollection ? 'collection.list.searchAria' : 'category.list.searchAria',
          )}
          data-testid="categories-search"
          className="w-[260px]"
        />

        <NativeSelect
          className="w-auto"
          value={statusFilter}
          aria-label={t('category.list.filterStatusAria')}
          data-testid="categories-filter-status"
          onChange={(e) => patchParams({ status: e.target.value })}
        >
          <option value="">{t('category.list.filterStatusAll')}</option>
          <option value="Active">{t('category.list.statusActive')}</option>
          <option value="Inactive">{t('category.list.statusInactive')}</option>
        </NativeSelect>

        <NativeSelect
          className="w-auto"
          value={displayFilter}
          aria-label={t('category.list.filterDisplayAria')}
          data-testid="categories-filter-display"
          onChange={(e) => patchParams({ display: e.target.value })}
        >
          <option value="">{t('category.list.filterDisplayAll')}</option>
          <option value="on">{t('category.list.displayOn')}</option>
          <option value="off">{t('category.list.displayOff')}</option>
        </NativeSelect>

        {loading ? (
          <span
            className="ml-auto flex items-center gap-2 text-sm text-pw-text-muted"
            role="status"
            data-testid="categories-busy"
          >
            <Spinner size="sm" />
            {t('category.list.working')}
          </span>
        ) : null}

        <Button
          type="button"
          variant="default"
          className={cn('font-semibold', loading ? '' : 'ml-auto')}
          data-testid="categories-create-btn"
          onClick={() => navigate(`${basePath}/new`)}
        >
          + {isCollection ? t('collection.actions.create') : t('category.actions.create')}
        </Button>
      </div>

      {/* Table */}
      {activeQuery.isLoading ? (
        <div
          className="flex items-center justify-center rounded-lg border border-pw-border bg-pw-bg py-16"
          data-testid="categories-loading"
        >
          <Spinner />
        </div>
      ) : activeQuery.isError ? (
        <div
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-300"
          data-testid="categories-load-error"
        >
          {t('category.page.loadError')}{' '}
          <button type="button" onClick={() => void activeQuery.refetch()} className="underline">
            {t('category.page.retry')}
          </button>
        </div>
      ) : (activeQuery.data?.items.length ?? 0) === 0 ? (
        <div
          className="rounded-lg border border-dashed border-pw-border py-16 text-center text-sm text-pw-text-muted"
          data-testid="categories-empty"
        >
          {isCollection ? t('collection.list.empty') : t('category.list.empty')}
        </div>
      ) : isCollection ? (
        <TaxonomyTable<CollectionRow>
          rows={(collectionQuery.data?.items ?? []) as CollectionRow[]}
          showLabel
          testIdPrefix="collections"
          onToggleStatus={handleToggleStatus}
          onToggleDisplay={handleToggleDisplay}
          onEdit={handleEdit}
          onViewProducts={handleViewProducts}
          onReorderSiblings={handleReorder}
          busy={busy}
        />
      ) : (
        <TaxonomyTable<CategoryRow>
          rows={(categoryQuery.data?.items ?? []) as CategoryRow[]}
          testIdPrefix="categories"
          onToggleStatus={handleToggleStatus}
          onToggleDisplay={handleToggleDisplay}
          onEdit={handleEdit}
          onViewProducts={handleViewProducts}
          onReorderSiblings={handleReorder}
          busy={busy}
        />
      )}

      {/* Footer — 3 zones per the design */}
      {!activeQuery.isLoading && !activeQuery.isError && total > 0 ? (
        <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-3">
          <div className="flex items-center gap-2 text-[13px] text-pw-text-muted">
            <span>{t('category.list.rowsPerPage')}</span>
            <NativeSelect
              className="w-auto"
              value={String(pageSize)}
              aria-label={t('category.list.rowsPerPage')}
              data-testid="categories-page-size"
              onChange={(e) => patchParams({ pageSize: e.target.value })}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </NativeSelect>
          </div>

          <span
            className="text-center text-[13px] text-pw-text-muted"
            data-testid="categories-range"
          >
            {t(isCollection ? 'collection.list.showingRange' : 'category.list.showingRange', {
              from: (page - 1) * pageSize + 1,
              to: Math.min(page * pageSize, total),
              total,
            })}
          </span>

          <nav
            className="flex items-center justify-end gap-1"
            aria-label={t('category.list.paginationAria')}
            data-testid="categories-pagination"
          >
            <PageButton
              disabled={page <= 1 || busy}
              onClick={() => patchParams({ page: String(page - 1) }, false)}
              label={t('category.list.prevPage')}
              testId="categories-page-prev"
            >
              ‹
            </PageButton>
            {buildPageWindow(page, pageCount).map((p, i) =>
              p === null ? (
                <span key={`gap-${i}`} className="px-1 text-[13px] text-pw-text-muted">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  aria-current={p === page ? 'page' : undefined}
                  disabled={busy}
                  onClick={() => patchParams({ page: String(p) }, false)}
                  data-testid={`categories-page-${p}`}
                  className={cn(
                    'inline-flex h-10 min-w-10 items-center justify-center rounded-md border px-3 text-sm transition-colors',
                    p === page
                      ? 'border-pw-primary bg-pw-primary text-white'
                      : 'border-pw-border text-pw-text hover:bg-pw-bg-hover',
                  )}
                >
                  {p}
                </button>
              ),
            )}
            <PageButton
              disabled={page >= pageCount || busy}
              onClick={() => patchParams({ page: String(page + 1) }, false)}
              label={t('category.list.nextPage')}
              testId="categories-page-next"
            >
              ›
            </PageButton>
          </nav>
        </div>
      ) : null}

      {/* Editing links straight from the list persists through the same update
          mutation the toggles use, so it reports success/failure identically. */}
      <SelectProductsDialog
        open={productsFor !== null}
        onOpenChange={(next) => {
          if (!next) setProductsFor(null);
        }}
        value={productsFor?.ids ?? []}
        // The row being edited owns its links (dbg-20260811-180000).
        {...(productsFor ? { ownerCategoryId: productsFor.id } : {})}
        // Collections are a grouping: the same product may be in several, and
        // its category says nothing about that (dbg-20260813).
        exclusive={!isCollection}
        defaultShowSelectedOnly
        onSave={(nextIds) => {
          if (!productsFor) return;
          toggleMutation.mutate({ id: productsFor.id, dto: { blueprintIds: nextIds } });
          setProductsFor(null);
        }}
      />
    </main>
  );
}

// ---------------------------------------------------------------------------
// Footer helpers
// ---------------------------------------------------------------------------

function PageButton({
  disabled,
  onClick,
  label,
  testId,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  label: string;
  testId: string;
  children: JSX.Element | string;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-pw-border text-pw-text transition-colors hover:bg-pw-bg-hover disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/** Windowed page list — `null` renders an ellipsis. */
function buildPageWindow(current: number, count: number): Array<number | null> {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const out: Array<number | null> = [1];
  const from = Math.max(2, current - 1);
  const to = Math.min(count - 1, current + 1);
  if (from > 2) out.push(null);
  for (let p = from; p <= to; p += 1) out.push(p);
  if (to < count - 1) out.push(null);
  out.push(count);
  return out;
}

export default CategoriesPage;
